import React from "react";
import { Select, Typography } from "antd";
import type { Organization } from "../../../../api/v1";
import { shouldShowApproveRejectReceipt } from "../utili";

const { Text } = Typography;

export type ReceiptAction = {
  id: string;
  value: string;
  label: string;
};

export const RECEIPT_ACTION_BOUNCE: ReceiptAction = {
  id: "mark_as_bounced",
  value: "mark_as_bounced",
  label: "Mark as Bounced",
};

export const RECEIPT_ACTION_REPLACE_CHEQUE: ReceiptAction = {
  id: "replace_cheque",
  value: "replace_cheque",
  label: "Replace Cheque",
};

export const RECEIPT_ACTION_CLEAR: ReceiptAction = {
  id: "mark_cleared",
  value: "mark_cleared",
  label: "Mark as Cleared",
};

export const RECEIPT_ACTION_DEFER: ReceiptAction = {
  id: "defer_cheque_deposit",
  value: "defer_cheque_deposit",
  label: "Defer Cheque Deposit",
};

export const RECEIPT_ACTION_RECALL: ReceiptAction = {
  id: "recallCheque",
  value: "recallCheque",
  label: "Recall Cheque",
};

export const RECEIPT_ACTION_RECALL_APPROVE_REJECT: ReceiptAction = {
  id: "approveRejectRecallCheque",
  value: "approveRejectRecallCheque",
  label: "Approve/Reject Recall Cheque",
};

export const RECEIPT_ACTION_HISTORY: ReceiptAction = {
  id: "history",
  value: "history",
  label: "History",
};

export const RECEIPT_ACTION_PRINT: ReceiptAction = {
  id: "printReceipt",
  value: "printReceipt",
  label: "Print Receipt",
};

export const RECEIPT_ACTION_APPROVE_REJECT: ReceiptAction = {
  id: "approveRejectReceipt",
  value: "approveRejectReceipt",
  label: "Approve/Reject Receipt",
};

export const buildReceiptActions = (
  row: any,
  organization?: Organization,
  canApproveReceipts?: boolean,
): ReceiptAction[] => {
  if (
    shouldShowApproveRejectReceipt(row, organization, canApproveReceipts, false) &&
    row?.paymentStatus?.toLowerCase() === "pending"
  ) {
    return [RECEIPT_ACTION_APPROVE_REJECT, RECEIPT_ACTION_HISTORY];
  }

  const paymentStatusValue = typeof row?.paymentStatus === "string" ? row.paymentStatus.trim().toLowerCase() : "";

  if (paymentStatusValue === "cancelled") {
    return [RECEIPT_ACTION_HISTORY];
  }
  if (paymentStatusValue === "recalled") {
    return [RECEIPT_ACTION_PRINT, RECEIPT_ACTION_HISTORY];
  }

  const isCleared =
    row?.isCleared === true ||
    row?.status?.toLowerCase?.() === "cleared" ||
    row?.paymentDetails?.status?.toLowerCase?.() === "cleared";
  const rawMode = row?.modeOfPayment ?? row?.paymentMode;
  const mode = typeof rawMode === "string" ? rawMode.trim().toLowerCase().replace(/\s+/g, "_") : "";
  const isCheque = mode === "cheque";

  const recallStatus = row?.recallChequeRequestStatus === "pending" || row?.recall?.status === "pending";
  const isRecallPending = recallStatus;

  const showClear = row?.isCleared !== true && paymentStatusValue === "paid" && !isRecallPending;

  const rawPaidStatus = row?.paymentStatus ?? "";
  const status = typeof rawPaidStatus === "string" ? rawPaidStatus.trim().toLowerCase() : "";
  // =========== According to story 14668 ===========
  const isDeferred = status === "pending" && isCheque;

  // Non-cheque: Clear (when paid / not cleared / no pending recall) + Print + History
  if (!showClear) {
    return [...(showClear ? [RECEIPT_ACTION_CLEAR] : []), RECEIPT_ACTION_PRINT, RECEIPT_ACTION_HISTORY];
  }

  const rawBounceStatus =
    row?.paymentStatus ?? row?.chequeStatus ?? row?.receiptStatus ?? row?.paymentDetails?.status ?? row?.status ?? "";
  const bounceStatus =
    typeof rawBounceStatus === "string"
      ? rawBounceStatus.trim().toLowerCase() === "bounced"
        ? "bounce"
        : rawBounceStatus.trim().toLowerCase()
      : "";
  const isBounced = bounceStatus === "bounce";

  // if (isBounced && !isRecallPending) {
  //   return [RECEIPT_ACTION_REPLACE_CHEQUE, RECEIPT_ACTION_PRINT, RECEIPT_ACTION_HISTORY];
  // }

  if (isBounced && !isRecallPending) {
    return [RECEIPT_ACTION_PRINT, RECEIPT_ACTION_HISTORY];
  }
  // Always allow History on receipts
  if (isCleared && !isRecallPending) return [RECEIPT_ACTION_PRINT, RECEIPT_ACTION_HISTORY];

  const showBounce = isCheque && paymentStatusValue === "paid" && !isRecallPending;
  const showRecall = isCheque && paymentStatusValue === "paid" && !isCleared && !isRecallPending;

  const deferAction = isDeferred ? [RECEIPT_ACTION_DEFER] : [];
  return [
    ...(showBounce ? [RECEIPT_ACTION_BOUNCE] : []),
    ...(showClear ? [RECEIPT_ACTION_CLEAR] : []),
    ...deferAction,
    ...(isRecallPending ? [RECEIPT_ACTION_RECALL_APPROVE_REJECT] : showRecall ? [RECEIPT_ACTION_RECALL] : []),
    RECEIPT_ACTION_PRINT,
    RECEIPT_ACTION_HISTORY,
  ];
};

type ReceiptRowActionsProps = {
  row: any;
  onAction: (action: string, row: any) => void;
  organization?: Organization;
  canApproveReceipts?: boolean;
};

export const ReceiptRowActions: React.FC<ReceiptRowActionsProps> = ({
  row,
  onAction,
  organization,
  canApproveReceipts,
}) => {
  const options = buildReceiptActions(row, organization, canApproveReceipts);

  const handleChange = (value: string) => {
    if (!value) return;

    onAction(value, row);
  };

  return (
    <Select
      onChange={handleChange}
      className="Sentence"
      placeholder="Select an action"
      style={{ width: 180 }}
      value={null}
      allowClear
    >
      {options.map((option) => (
        <Select.Option key={option.id} value={option.value}>
          <Text>{option.label}</Text>
        </Select.Option>
      ))}
    </Select>
  );
};
